import { type IconProps } from '@kijiji/icons/src/components/Icon'
import { ChevronRightIcon } from '@kijiji/icons/src/icons'
import { useQRCode } from 'next-qrcode'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { ShowAtOrLarger } from '@/components/shared/breakpoint'
import {
  CanvasBottomBodyText,
  CanvasBottomWrapper,
  CanvasTitle,
  CanvasWrapper,
} from '@/components/shared/global-footer/footer-app-banner/styled'
import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { BodyText } from '@/ui/atoms/body-text'

type AppBannerQRCanvasLinkProps = {
  /**
   * Defines the function to be triggered on link click
   */
  handleClick: () => void
  /**
   * Link to a page to download the app
   */
  href: string
  /**
   * Defines user device to decide which links to show
   */
  isPhoneOrTabletDevice: boolean
  /**
   * Aria label for the store link
   */
  linkAriaLabel: string
  /**
   * Link label to take user to download the app
   */
  linkLabel: string
  /**
   * Logo to provider store for the app to be downloaded
   */
  LogoIcon: FC<IconProps>
  /**
   * Description to show up above the QR code along with the icon
   */
  qrCodeDescription: string
}

export const AppBannerQRCanvasLink: FC<AppBannerQRCanvasLinkProps> = ({
  LogoIcon,
  handleClick,
  href,
  isPhoneOrTabletDevice,
  linkAriaLabel,
  linkLabel,
  qrCodeDescription,
}) => {
  const { colors } = useTheme()
  const { Canvas } = useQRCode()

  const canvasOptions = {
    level: 'M',
    margin: 0,
    scale: 5,
    width: 100,
    color: { dark: colors.purple.light4, light: colors.grey.primary },
  }

  return (
    <CanvasWrapper data-testid="qrcode-wrapper">
      <CanvasTitle>
        <LogoIcon size="xsm" />

        <BodyText size="medium" color={colors.purple.light4}>
          {qrCodeDescription}
        </BodyText>
      </CanvasTitle>

      <Canvas text={href} options={canvasOptions} />

      {/* It should only show the link if the breakpoint is large on mobile devices */}
      {!isPhoneOrTabletDevice ? (
        <ShowAtOrLarger breakpoint="large">
          <LinkCustom
            data-testid="canvas-link"
            aria-label={linkAriaLabel}
            href={href}
            onClick={handleClick}
            rel="noopener noreferrer"
            target="_blank"
            noStyle
          >
            <CanvasBottomWrapper>
              <CanvasBottomBodyText size="medium">{linkLabel}</CanvasBottomBodyText>
              <ChevronRightIcon color={colors.white} />
            </CanvasBottomWrapper>
          </LinkCustom>
        </ShowAtOrLarger>
      ) : null}
    </CanvasWrapper>
  )
}
